/**
 * /tools/fixed-bookend?clientId=X — STP-035.
 *
 * The AM (5 min) + PM (3-5 min) ritual structure that **locks at Step 4**.
 * The submitted template drives the daily `/check-in` AM tab from Step 4
 * onward. Persists via the generic tools route → versioned `client_documents`
 * doc_type=`bookend_template`.
 *
 * batch #4 (#7): stripped to the AM + PM rituals themselves + a single
 * "Intention" note (what these rituals are addressing). The identity
 * statement, per-virtue affirmations and ONE Thing prompt moved out — ONE
 * Thing is now its own daily check-in card (batch #4 #8).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

interface RitualStep {
  label: string;
  durationMin: string;
}

interface FormState {
  amSteps: RitualStep[];
  pmSteps: RitualStep[];
  intention: string;
}

const DEFAULT_AM: RitualStep[] = [
  { label: '', durationMin: '1' },
  { label: '', durationMin: '2' },
];
const DEFAULT_PM: RitualStep[] = [
  { label: '', durationMin: '2' },
  { label: '', durationMin: '1' },
];

const DEFAULT_FORM: FormState = {
  amSteps: DEFAULT_AM.map((s) => ({ ...s })),
  pmSteps: DEFAULT_PM.map((s) => ({ ...s })),
  intention: '',
};

/** Stable snapshot — drives the "saved & unchanged → disable" button state. */
function snapshot(form: FormState): string {
  return JSON.stringify(form);
}

/**
 * #10 — surface the saved AM/PM ritual steps in the daily tracker: each AM step
 * becomes an `am_bookend` item (first actions of the day), each PM step a
 * `pm_bookend` item (last actions). Idempotent — only labels not already
 * present in that section are created, so re-saving never duplicates. Best
 * effort: tracker errors never block the bookend save itself.
 */
async function syncBookendTrackingItems(
  accessToken: string,
  clientId: string,
  amLabels: string[],
  pmLabels: string[],
): Promise<void> {
  let existing: api.TrackingItem[];
  try {
    existing = await api.listTrackingItems(accessToken, clientId);
  } catch {
    return;
  }
  const present = (category: api.TrackingItem['category'], label: string): boolean =>
    existing.some(
      (it) =>
        it.category === category &&
        it.archivedAt === null &&
        it.label.trim().toLowerCase() === label.trim().toLowerCase(),
    );
  const toCreate: { label: string; category: 'am_bookend' | 'pm_bookend' }[] = [];
  for (const label of amLabels) {
    if (label.trim() && !present('am_bookend', label)) {
      toCreate.push({ label: label.trim(), category: 'am_bookend' });
    }
  }
  for (const label of pmLabels) {
    if (label.trim() && !present('pm_bookend', label)) {
      toCreate.push({ label: label.trim(), category: 'pm_bookend' });
    }
  }
  for (const item of toCreate) {
    try {
      await api.createTrackingItem(accessToken, clientId, {
        label: item.label,
        category: item.category,
        itemType: 'yes_no',
      });
    } catch {
      /* best-effort — keep going */
    }
  }
}

export function FixedBookendPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'fixed-bookend');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const next: FormState = { ...DEFAULT_FORM };
          next.amSteps = parseSteps(p.amSteps) ?? next.amSteps;
          next.pmSteps = parseSteps(p.pmSteps) ?? next.pmSteps;
          // `intention` is the batch #4 field; fall back to the legacy
          // `reflection` so templates saved before the strip still show.
          if (typeof p.intention === 'string') next.intention = p.intention;
          else if (typeof p.reflection === 'string') next.intention = p.reflection;
          setForm(next);
          setSavedAt(existing.completed_at ?? null);
          setSavedSnapshot(snapshot(next));
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const stepsValid = (steps: RitualStep[]): boolean =>
    steps.length >= 1 &&
    steps.length <= 10 &&
    steps.every((s) => s.label.trim().length > 0 && s.durationMin.trim().length > 0);

  const canSubmit =
    !submitting && hydrated && stepsValid(form.amSteps) && stepsValid(form.pmSteps);
  // Saved and unchanged → the button locks (the standard tool behavior); no
  // separate read-only "Edit" mode any more.
  const isSavedClean = savedAt !== null && savedSnapshot === snapshot(form);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.fixedBookend.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const payload = {
      amSteps: form.amSteps.map((s) => ({
        label: s.label.trim(),
        durationMin: Number(s.durationMin) || 0,
      })),
      pmSteps: form.pmSteps.map((s) => ({
        label: s.label.trim(),
        durationMin: Number(s.durationMin) || 0,
      })),
      ...(form.intention.trim() ? { intention: form.intention.trim() } : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'fixed-bookend',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
      setSavedSnapshot(snapshot(form));
      // #10 — push the filled AM/PM ritual steps into the daily tracker as the
      // first / last actions of the day (idempotent, best-effort).
      await syncBookendTrackingItems(
        accessToken,
        clientId,
        payload.amSteps.map((s) => s.label),
        payload.pmSteps.map((s) => s.label),
      );
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.fixedBookend.errors.generic'));
      } else {
        setSubmitError(t('tools.fixedBookend.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section
        className="max-w-2xl mx-auto px-s5 py-s8"
        data-testid="fixed-bookend-missing-client"
      >
        <h1 className="font-display text-display-lg text-ink">{t('tools.fixedBookend.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('tools.fixedBookend.errors.missingClient')}</p>
      </section>
    );
  }

  const setStep = (
    list: 'amSteps' | 'pmSteps',
    idx: number,
    key: keyof RitualStep,
    value: string,
  ): void => {
    setForm((prev) => ({
      ...prev,
      [list]: prev[list].map((s, i) => (i === idx ? { ...s, [key]: value } : s)),
    }));
  };
  const addStep = (list: 'amSteps' | 'pmSteps'): void => {
    setForm((prev) =>
      prev[list].length >= 10
        ? prev
        : { ...prev, [list]: [...prev[list], { label: '', durationMin: '1' }] },
    );
  };
  const removeStep = (list: 'amSteps' | 'pmSteps', idx: number): void => {
    setForm((prev) =>
      prev[list].length <= 1
        ? prev
        : { ...prev, [list]: prev[list].filter((_, i) => i !== idx) },
    );
  };

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.fixedBookend.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.fixedBookend.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">{t('tools.fixedBookend.description')}</p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        <RitualSection
          title={t('tools.fixedBookend.am.title')}
          hint={t('tools.fixedBookend.am.hint')}
          steps={form.amSteps}
          listKey="amSteps"
          onSetStep={setStep}
          onAdd={() => addStep('amSteps')}
          onRemove={(i) => removeStep('amSteps', i)}
          testidPrefix="fixed-bookend-am"
        />

        <RitualSection
          title={t('tools.fixedBookend.pm.title')}
          hint={t('tools.fixedBookend.pm.hint')}
          steps={form.pmSteps}
          listKey="pmSteps"
          onSetStep={setStep}
          onAdd={() => addStep('pmSteps')}
          onRemove={(i) => removeStep('pmSteps', i)}
          testidPrefix="fixed-bookend-pm"
        />

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">
              {t('tools.fixedBookend.intention.label')}
              <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
                {t('tools.fixedBookend.optional')}
              </span>
            </span>
            <p className="text-xs text-gray-1 mb-s2 mt-s1">{t('tools.fixedBookend.intention.hint')}</p>
            <textarea
              value={form.intention}
              onChange={(e) => setForm((prev) => ({ ...prev, intention: e.target.value }))}
              placeholder={t('tools.fixedBookend.placeholders.intention')}
              className="input-field mt-s2 min-h-20"
              data-testid="fixed-bookend-intention"
              rows={3}
            />
          </label>
        </article>

        {submitError && (
          <p role="alert" className="form-error" data-testid="fixed-bookend-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p
            role="status"
            className="text-sm text-flame-deep flex items-center gap-s2"
            data-testid="fixed-bookend-saved-at"
          >
            <span aria-hidden="true">✓</span>
            {t('tools.fixedBookend.saved.savedOn', { when: formatSavedAt(savedAt, i18n.language) })}
          </p>
        )}

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={!canSubmit || isSavedClean}
          data-testid="fixed-bookend-submit"
        >
          {submitting
            ? t('tools.fixedBookend.submitting')
            : isSavedClean
              ? t('tools.fixedBookend.savedButton')
              : t('tools.fixedBookend.submit')}
        </button>
        <FinalizeWarning />
      </form>
    </section>
  );
}

function formatSavedAt(iso: string, locale: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(0, 19).replace('T', ' ');
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(d);
  } catch {
    return iso.slice(0, 19).replace('T', ' ');
  }
}

interface RitualSectionProps {
  title: string;
  hint: string;
  steps: RitualStep[];
  listKey: 'amSteps' | 'pmSteps';
  onSetStep: (
    list: 'amSteps' | 'pmSteps',
    idx: number,
    key: keyof RitualStep,
    value: string,
  ) => void;
  onAdd: () => void;
  onRemove: (idx: number) => void;
  testidPrefix: string;
}

function RitualSection({
  title,
  hint,
  steps,
  listKey,
  onSetStep,
  onAdd,
  onRemove,
  testidPrefix,
}: RitualSectionProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
      <h2 className="font-display text-display-sm text-ink">{title}</h2>
      <p className="mt-s2 text-gray-2 italic">{hint}</p>
      <ol className="mt-s3 space-y-s3">
        {steps.map((step, idx) => (
          <li key={idx} className="grid grid-cols-12 gap-s2 items-start">
            <span className="col-span-1 font-mono text-sm text-gray-1 mt-s2">{idx + 1}.</span>
            <input
              type="text"
              value={step.label}
              onChange={(e) => onSetStep(listKey, idx, 'label', e.target.value)}
              placeholder={t('tools.fixedBookend.placeholders.stepLabel')}
              className="input-field col-span-7"
              data-testid={`${testidPrefix}-label-${idx}`}
            />
            <input
              type="number"
              min={0}
              max={60}
              value={step.durationMin}
              onChange={(e) => onSetStep(listKey, idx, 'durationMin', e.target.value)}
              placeholder={t('tools.fixedBookend.placeholders.durationMin')}
              className="input-field col-span-2"
              data-testid={`${testidPrefix}-duration-${idx}`}
            />
            <button
              type="button"
              onClick={() => onRemove(idx)}
              disabled={steps.length <= 1}
              className="col-span-2 btn-ghost text-sm"
              data-testid={`${testidPrefix}-remove-${idx}`}
            >
              ×
            </button>
          </li>
        ))}
      </ol>
      <button
        type="button"
        onClick={onAdd}
        disabled={steps.length >= 10}
        className="btn-ghost mt-s3"
        data-testid={`${testidPrefix}-add`}
      >
        + {t('tools.fixedBookend.steps.add')}
      </button>
    </article>
  );
}

function parseSteps(raw: unknown): RitualStep[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  return raw.map((entry) => {
    const s = entry as Record<string, unknown>;
    return {
      label: typeof s.label === 'string' ? s.label : '',
      durationMin: typeof s.durationMin === 'number' ? String(s.durationMin) : '',
    };
  });
}
